import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  validateEmail,
  validateName,
  validatePhone,
} from "../../common/validation";
import { addClientManagement } from "../../redux/slice/clientmanagementSlice";
import Loader from "../../common/Loader";
import AddSuccessModal from "../../components/Modals/AddSuccessModal";
import { ErrorModal } from "../../components/Modals/ErrorModal";
import { getCountryCodes } from "../../redux/slice/commonSlice";

const AddInvestorClient = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { loading, error } = useSelector((state) => state.clientManagement);
  const { advisors } = useSelector((state) => state.advisorManagement);
  const { countryCodes, codeLoading } = useSelector((state) => state.common);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone: "",
    relationship: "",
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedCountry, setSelectedCountry] = useState({
    label: "United States",
    phone: "1",
    phoneLength: 10,
  });

  const relationshipOptions = [
    { value: "", label: "-Select-" },
    { value: "father", label: "Father" },
    { value: "mother", label: "Mother" },
    { value: "brother", label: "Brother" },
    { value: "sister", label: "Sister" },
    { value: "son", label: "Son" },
    { value: "relative", label: "Relative" },
    { value: "other", label: "Other" },
  ];

  useEffect(() => {
    dispatch(getCountryCodes());
  }, [dispatch]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    let error = "";
    let processedValue = value;

    if (id === "firstname" || id === "lastname") {
      error = validateName(value, id === "firstname" ? "First Name" : "Last Name");
    } else if (id === "email") {
      processedValue = processedValue.toLowerCase().trim();
      error = validateEmail(value);
    } else if (id === "phone") {
      processedValue = value.replace(/\D/g, "");
      error = validatePhone(processedValue, selectedCountry.phoneLength, selectedCountry.label);
    } else if (id === "relationship") {
      error = value === "" ? "Please select a relationship" : "";
    }

    setFormData((prev) => ({
      ...prev,
      [id]: processedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [id]: error,
    }));
  };

  const handleCancel = () => {
   navigate(`/advisor-details/${id}`, { state: { tab: "investors" } });

  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = {
      firstname: validateName(formData.firstname, "First Name"),
      lastname: validateName(formData.lastname, "Last Name"),
      email: validateEmail(formData.email),
      relationship: formData.relationship === "" ? "Please select a relationship" : "",
    };

    if (formData.phone.trim() !== "") {
      validationErrors.phone = validatePhone(
        formData.phone,
        selectedCountry.phoneLength,
        selectedCountry.label
      );
    }

    const hasErrors = Object.values(validationErrors).some((err) => err !== "");
    if (hasErrors) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      first_name: formData.firstname.trim(),
      last_name: formData.lastname.trim(),
      email: formData.email.trim(),
      phone_number: formData.phone.trim(),
      relationship: formData.relationship,
      advisor_id: Number(id), // always from URL param
      password_hash: "123",
      user_type: "client",
      country_code: selectedCountry.phone,
    };

    dispatch(addClientManagement(payload))
      .unwrap()
      .then(() => {
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
       navigate(`/advisor-details/${id}`, { state: { tab: "investors" } });

        }, 2000);
      })
      .catch(() => {
        setShowErrorModal(true);
        setTimeout(() => {
          setShowErrorModal(false);
        }, 2000);
      });
  };

  const handleDisable = () => {
    return (
      Object.values(errors).some((error) => error !== "") ||
      formData.firstname.trim() === "" ||
      formData.lastname.trim() === "" ||
      formData.email.trim() === "" ||
      formData.relationship === ""
    );
  };

  const selectedAdvisor = advisors?.find((a) => a.id === Number(id));

  return (
    <>
      {(loading || codeLoading) && <Loader />}
      <div className="page__body">
        <div className="details">
          <div className="form">
            <section className="form__section">
              <h1 className="form__section__title">Add New Client</h1>

              {/* First Name + Last Name */}
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="firstname" className="form__label">
                      First Name<sup className="form__label__asterisk">*</sup>
                    </label>
                    <input
                      type="text"
                      id="firstname"
                      maxLength={50}
                      value={formData.firstname}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="First name"
                    />
                    {errors.firstname && <p className="form__error">{errors.firstname}</p>}
                  </div>
                </div>

                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="lastname" className="form__label">
                      Last Name<sup className="form__label__asterisk">*</sup>
                    </label>
                    <input
                      type="text"
                      id="lastname"
                      maxLength={50}
                      value={formData.lastname}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="Last Name"
                    />
                    {errors.lastname && <p className="form__error">{errors.lastname}</p>}
                  </div>
                </div>
              </div>

              {/* Email + Phone */}
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="email" className="form__label">
                      Email<sup className="form__label__asterisk">*</sup>
                    </label>
                    <input
                      type="email"
                      id="email"
                      maxLength={100}
                      value={formData.email}
                      onChange={handleChange}
                      className="form__input form__input--sm"
                      placeholder="Email"
                    />
                    {errors.email && <p className="form__error">{errors.email}</p>}
                  </div>
                </div>

                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="phone" className="form__label">
                      Phone Number
                    </label>
                    <div className="form__groupField form__groupField--phonenumber">
                      <select
                        className="form__select form__select--sm"
                        value={selectedCountry.phone}
                        onChange={(e) => {
                          const selected = countryCodes.find((c) => c.phone === e.target.value);
                          if (selected) setSelectedCountry(selected);
                        }}
                      >
                        {countryCodes?.map((code, i) => (
                          <option key={i} value={code.phone}>
                            +{code.phone}
                          </option>
                        ))}
                      </select>

                      <input
                        type="tel"
                        id="phone"
                        className="form__input form__input--sm"
                        placeholder="Phone Number"
                        inputMode="numeric"
                        maxLength={selectedCountry.phoneLength}
                        value={formData.phone}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.phone && <p className="form__error">{errors.phone}</p>}
                  </div>
                </div>
              </div>

              {/* Relationship + Advisor */}
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="relationship" className="form__label">
                      Relationship<sup className="form__label__asterisk">*</sup>
                    </label>
                    <select
                      id="relationship"
                      value={formData.relationship}
                      onChange={handleChange}
                      className="form__select form__select--sm"
                    >
                      {relationshipOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.relationship && <p className="form__error">{errors.relationship}</p>}
                  </div>
                </div>

                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">
                      Advisor<sup className="form__label__asterisk">*</sup>
                    </label>
                    <select className="form__select form__select--sm" value={id} disabled>
                      {selectedAdvisor ? (
                        <option value={selectedAdvisor.id}>
                          {`${selectedAdvisor.first_name || ""} ${selectedAdvisor.last_name || ""}`}
                        </option>
                      ) : (
                        <option>Loading advisor...</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="btn__group">
                <button className="btn btn__white" type="button" onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  type="submit"
                  disabled={handleDisable()}
                  onClick={handleSubmit}
                >
                  Send Invite
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>

      {showSuccessModal && (
        <AddSuccessModal view={"client"} onClose={() => setShowSuccessModal(false)} />
      )}
      {showErrorModal && (
        <ErrorModal view={error} onClose={() => setShowErrorModal(false)} raw={true} />
      )}
    </>
  );
};

export default AddInvestorClient;
